import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import SubcontratadoDocumentos, { validarAnexosSubcontratado } from '../components/SubcontratadoDocumentos';

/* ============================================================
   SST DE SUBCONTRATADOS (SGI — ISO 45001, 8.1.4)
   Gestão dos subcontratados + link público para carregarem os
   documentos de SST (seguros, aptidão médica, CV…). As validades
   alimentam o painel de validades.
   ============================================================ */

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };
const validadeCor = (d) => {
    if (!d) return null;
    const dias = Math.floor((new Date(d).getTime() - Date.now()) / 86400000);
    if (dias < 0) return { bg: '#fee2e2', fg: '#991b1b', txt: `Expirado há ${-dias}d` };
    if (dias <= 30) return { bg: '#fef9c3', fg: '#854d0e', txt: `Expira em ${dias}d` };
    return { bg: '#dcfce7', fg: '#166534', txt: `Válido (${fmtData(d)})` };
};

/* ---------- Criar / editar subcontratado ---------- */
function SubcModal({ subc, tipos, onClose, onSaved }) {
    const novo = !subc;
    const [form, setForm] = useState({
        nome: subc?.nome || '', nif: subc?.nif || '', tipo_servico: subc?.tipo_servico || '',
        contacto: subc?.contacto || '', email: subc?.email || '', telefone: subc?.telefone || '',
        ativo: subc?.ativo ?? true, observacoes: subc?.observacoes || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [anexos, setAnexos] = useState([]);
    const [enviados, setEnviados] = useState([]);
    const [progresso, setProgresso] = useState('');
    const [subcontratadoId, setSubcontratadoId] = useState(subc?.id || null);
    const registoId = useRef(subc?.id || null);
    const aGuardar = useRef(false);
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const fechar = () => { if (!aGuardar.current) onClose(); };

    const guardar = async () => {
        if (aGuardar.current) return;
        if (!form.nome.trim()) { setErr('Indique o nome.'); return; }
        const erroAnexo = validarAnexosSubcontratado(anexos, tipos);
        if (erroAnexo) { setErr(erroAnexo); return; }
        aGuardar.current = true;
        setSaving(true); setErr(''); setProgresso('A guardar os dados do subcontratado…');
        let ficheiroAtual = null;
        try {
            if (registoId.current) await api.put(`/subcontratados/${registoId.current}`, form);
            else {
                const r = await api.post('/subcontratados', form);
                // Mantém o registo criado se um envio falhar, para a repetição não criar outro.
                registoId.current = r.data.id;
                setSubcontratadoId(r.data.id);
            }
            for (const [indice, anexo] of anexos.entries()) {
                ficheiroAtual = anexo.ficheiro.name;
                setProgresso(`A enviar documento ${indice + 1} de ${anexos.length}: ${ficheiroAtual}`);
                const dados = new FormData();
                dados.append('ficheiro', anexo.ficheiro);
                dados.append('tipo', anexo.tipo);
                if (anexo.data_emissao) dados.append('data_emissao', anexo.data_emissao);
                if (anexo.data_validade) dados.append('data_validade', anexo.data_validade);
                if (anexo.observacoes.trim()) dados.append('observacoes', anexo.observacoes.trim());
                const r = await api.post(`/subcontratados/${registoId.current}/documento`, dados, { timeout: 120000 });
                setEnviados(prev => [...prev, r.data]);
                setAnexos(prev => prev.filter(a => a.id !== anexo.id));
            }
            onSaved(); onClose();
        } catch (e) {
            const detalhe = e.response?.data?.error || 'Verifique a ligação e tente novamente.';
            setErr(ficheiroAtual
                ? `Os dados do subcontratado foram guardados, mas não foi possível enviar “${ficheiroAtual}”. ${detalhe} Prima Guardar para enviar os documentos pendentes.`
                : `Não foi possível guardar o subcontratado. ${detalhe}`);
            if (registoId.current) onSaved();
        } finally { aGuardar.current = false; setSaving(false); setProgresso(''); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={fechar} style={overlay} onClick={fechar}>
            <div className="subc-editor" style={{ ...modal, maxWidth: 760 }} onClick={e => e.stopPropagation()} aria-busy={saving}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? 'Novo subcontratado' : `Editar ${subc.nome}`}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" disabled={saving} onClick={fechar} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div className="subc-editor-body">
                    <fieldset className="subc-editor-fields" disabled={saving}>
                        <div style={{ marginBottom: 10 }}>
                            <label data-liquid-label="true" htmlFor="subc-nome" style={lbl}>Nome *</label>
                            <input id="subc-nome" value={form.nome} onChange={e => upd('nome', e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                        <div style={grid2}>
                            <div>
                                <label data-liquid-label="true" style={lbl}>NIF</label>
                                <input value={form.nif} onChange={e => upd('nif', e.target.value)} style={{ ...inp, width: '100%' }} />
                            </div>
                            <div>
                                <label data-liquid-label="true" style={lbl}>Tipo de serviço</label>
                                <input value={form.tipo_servico} onChange={e => upd('tipo_servico', e.target.value)} placeholder="ex.: Montagens" style={{ ...inp, width: '100%' }} />
                            </div>
                        </div>
                        <div className="subc-contact-grid" style={grid3}>
                            <div>
                                <label data-liquid-label="true" style={lbl}>Contacto</label>
                                <input value={form.contacto} onChange={e => upd('contacto', e.target.value)} style={{ ...inp, width: '100%' }} />
                            </div>
                            <div>
                                <label data-liquid-label="true" style={lbl}>Email</label>
                                <input value={form.email} onChange={e => upd('email', e.target.value)} style={{ ...inp, width: '100%' }} />
                            </div>
                            <div>
                                <label data-liquid-label="true" style={lbl}>Telefone</label>
                                <input value={form.telefone} onChange={e => upd('telefone', e.target.value)} style={{ ...inp, width: '100%' }} />
                            </div>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px', fontSize: '0.9rem', color: '#334155', cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.ativo} onChange={e => upd('ativo', e.target.checked)} /> Ativo (link funcional)
                        </label>
                        <div style={{ marginBottom: 6 }}>
                            <label data-liquid-label="true" style={lbl}>Observações</label>
                            <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                        </div>
                        <SubcontratadoDocumentos subcontratadoId={subcontratadoId} tipos={tipos} anexos={anexos} onChange={setAnexos} enviados={enviados} disabled={saving} />
                    </fieldset>
                </div>
                <div className="subc-editor-footer">
                    {err && <div role="alert" className="subc-documents-error">{err}</div>}
                    {progresso && <p role="status" className="subc-editor-progress">{progresso}</p>}
                    <div data-liquid-actions="true">
                        <button data-liquid-secondary="true" disabled={saving} onClick={fechar} style={btnSec}>Cancelar</button>
                        <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                    </div>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Documentos + link ---------- */
function DocsModal({ subc, tipos, onClose, onChanged }) {
    const [docs, setDocs] = useState(null);
    const [copiado, setCopiado] = useState(false);
    const link = `${window.location.origin}/subcontratado/${subc.token}`;
    const labelTipo = (t) => tipos.find(x => x.tipo === t)?.label || t;

    const carregar = useCallback(async () => {
        try { const r = await api.get(`/subcontratados/${subc.id}/documentos`); setDocs(r.data || []); }
        catch { setDocs([]); }
    }, [subc.id]);
    useEffect(() => { carregar(); }, [carregar]);

    const copiar = async () => {
        try { await navigator.clipboard.writeText(link); setCopiado(true); setTimeout(() => setCopiado(false), 2000); }
        catch { window.prompt('Copie o link:', link); }
    };
    const abrir = async (d) => {
        try {
            const res = await api.get(`/subcontratados/documento/${d.id}/ficheiro`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { alert('Erro ao abrir o ficheiro.'); }
    };
    const remover = async (d) => {
        if (!window.confirm('Eliminar este documento?')) return;
        try { await api.delete(`/subcontratados/documento/${d.id}`); carregar(); onChanged(); }
        catch { alert('Erro ao eliminar'); }
    };
    const regenerar = async () => {
        if (!window.confirm('Gerar um novo link? O link atual deixa de funcionar.')) return;
        try { await api.post(`/subcontratados/${subc.id}/regenerar-token`); onChanged(); onClose(); }
        catch { alert('Erro ao regenerar'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Documentos — {subc.nome}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <label data-liquid-label="true" style={lbl}>Link do subcontratado (envie por email/WhatsApp)</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input readOnly value={link} style={{ ...inp, flex: 1, background: '#f8fafc' }} />
                    <button data-liquid-secondary="true" onClick={copiar} style={btnSec}><LiquidText>{copiado ? '✓ Copiado' : 'Copiar'}</LiquidText></button>
                    <a href={`https://wa.me/?text=${encodeURIComponent('Carregue aqui os documentos de segurança para trabalhar com a SPROD: ' + link)}`}
                        target="_blank" rel="noreferrer" style={{ ...btnPri, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', background: '#25D366', border: 'none' }}>WhatsApp</a>
                </div>
                <button data-liquid-secondary="true" onClick={regenerar} style={{ ...btnSec, fontSize: '0.75rem', padding: '3px 10px', marginBottom: 16 }}><LiquidText>↻ Gerar novo link (invalida o atual)</LiquidText></button>

                {docs === null ? <p style={{ color: '#64748b' }}>A carregar…</p> : docs.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.88rem' }}>Ainda sem documentos carregados.</p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '4px 6px' }}>Documento</th>
                                <th style={{ padding: '4px 6px' }}>Validade</th>
                                <th style={{ padding: '4px 6px' }}>Carregado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.map(d => {
                                const v = validadeCor(d.data_validade);
                                return (
                                    <tr key={d.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                        <td style={{ padding: '6px' }}>
                                            <div style={{ fontWeight: 600, color: '#334155' }}>{labelTipo(d.tipo)}</div>
                                            <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>{d.nome_original}</div>
                                        </td>
                                        <td style={{ padding: '6px' }}>
                                            {v ? <span data-liquid-tone={liquidTone(v.bg)} data-liquid-badge="true" style={{ ...badge, background: v.bg, color: v.fg }}>{v.txt}</span> : '—'}
                                        </td>
                                        <td style={{ padding: '6px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtData(d.carregado_em)}</td>
                                        <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <button data-liquid-secondary="true" onClick={() => abrir(d)} style={{ ...btnSec, padding: '3px 9px', fontSize: '0.75rem' }}>Abrir</button>
                                            <button data-liquid-secondary="true" onClick={() => remover(d)} style={{ ...btnSec, padding: '3px 9px', fontSize: '0.75rem', color: '#e53e3e', marginLeft: 4 }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const Subcontratados = () => {
    const [lista, setLista] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [editar, setEditar] = useState(null);
    const [docs, setDocs] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, t] = await Promise.all([
                api.get('/subcontratados'),
                api.get('/subcontratados/tipos'),
            ]);
            setLista(r.data || []);
            setTipos(t.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const eliminar = async (s) => {
        if (!window.confirm(`Eliminar "${s.nome}" e os seus documentos?`)) return;
        try { await api.delete(`/subcontratados/${s.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    let visiveis = lista;
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(s => (s.nome || '').toLowerCase().includes(t) || (s.tipo_servico || '').toLowerCase().includes(t) || (s.nif || '').includes(t));
    }
    const semDocs = lista.filter(s => !s.n_docs).length;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Nome, serviço, NIF…" style={{ ...inp, width: 220 }} />
                    </div>
                    {semDocs > 0 && <span data-liquid-tone={liquidTone('#fef9c3')} data-liquid-badge="true" style={{ ...badge, background: '#fef9c3', color: '#854d0e', marginBottom: 6 }}>{semDocs} sem documentos</span>}
                </div>
                <button data-liquid-primary="true" onClick={() => setEditar({})} style={btnPri}>+ Novo subcontratado</button>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        {lista.length === 0 ? 'Sem subcontratados. Crie o primeiro e anexe os documentos ou envie-lhe o link para os carregar.' : 'Nenhum subcontratado corresponde à pesquisa.'}
                    </div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Subcontratado</th>
                                    <th>Serviço</th>
                                    <th style={{ textAlign: 'center' }}>Docs</th>
                                    <th>Validade + próxima</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map(s => {
                                    const v = validadeCor(s.proxima_validade);
                                    return (
                                        <tr key={s.id}>
                                            <td style={{ fontWeight: 600 }}>{s.nome}{s.nif && <div style={{ fontSize: '0.76rem', color: '#94a3b8', fontWeight: 400 }}>{s.nif}</div>}</td>
                                            <td>{s.tipo_servico || '—'}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span data-liquid-tone={liquidTone(s.n_docs ? '#dcfce7' : '#fee2e2')} data-liquid-badge="true" style={{ ...badge, background: s.n_docs ? '#dcfce7' : '#fee2e2', color: s.n_docs ? '#166534' : '#991b1b' }}>{s.n_docs}</span>
                                            </td>
                                            <td>{v ? <span data-liquid-tone={liquidTone(v.bg)} data-liquid-badge="true" style={{ ...badge, background: v.bg, color: v.fg }}>{v.txt}</span> : '—'}</td>
                                            <td><span data-liquid-tone={liquidTone(s.ativo ? '#dcfce7' : '#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: s.ativo ? '#dcfce7' : '#f1f5f9', color: s.ativo ? '#166534' : '#64748b' }}>{s.ativo ? 'Ativo' : 'Inativo'}</span></td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button data-liquid-primary="true" onClick={() => setDocs(s)} style={{ ...btnPri, ...mini }}>Docs & link</button>
                                                <button data-liquid-secondary="true" onClick={() => setEditar({ subc: s })} style={{ ...btnSec, ...mini }}>Editar</button>
                                                <button data-liquid-secondary="true" onClick={() => eliminar(s)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {editar && <SubcModal subc={editar.subc} tipos={tipos} onClose={() => setEditar(null)} onSaved={carregar} />}
            {docs && <DocsModal subc={docs} tipos={tipos} onClose={() => setDocs(null)} onChanged={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };

export default Subcontratados;
